/**
 * PendingWire Component
 *
 * Displays a wire that is pending approval in the My Pending Wires list.
 * Shows a "Pending Review" badge and the wire title with last saved timestamp.
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import WireTitle from './wire-title';

const Container = styled.section`
  ${props => (props.theme.isMobile ? `
    margin: 0 -20px;
    padding-left: 20px;
    padding-right: 20px;
    padding-top: 12px;
    padding-bottom: 7px;
  ` : `
    margin: 0px -26px;
    padding-left: 26px;
    padding-right: 26px;
    padding-bottom: 15px;
  `)};
  border-radius: 0px;
  border: none;
`;

const CaptionRow = styled.div`
  display: flex;
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: ${props => props.theme.fontWeightHeavy};
  ${props => (props.theme.isMobile ? `
    font-size: 10px;
    line-height: 18px;
    margin-bottom: 8px;
  ` : `
    font-size: 12px;
    line-height: 12px;
  `)};
  text-transform: uppercase;
  justify-content: space-between;
  align-content: center;

  > span.main {
    color: ${props => props.theme.primaryColor};
  }
`;

const PendingBadge = styled.div`
  background: #E67E22;
  color: #FFFFFF;
  width: auto;
  padding: 2px 8px;
  height: 19px;
  text-align: center;
  font-size: 12px;
  line-height: 15px;
  font-weight: ${props => props.theme.fontWeightHeavy};
  border-radius: 6px;
  margin-bottom: ${props => (props.theme.isMobile ? 8 : 0)}px;
  display: inline-block;
`;

const LastSaved = styled.div`
  color: ${props => props.theme.secondaryColor};
  font-size: ${props => (props.theme.isMobile ? 13 : 15)}px;
`;

const CategoryInfo = styled.div`
  font-size: ${props => (props.theme.isMobile ? 12 : 14)}px;
  margin-top: 4px;

  .category-label {
    color: ${props => props.theme.secondaryColor};
  }

  a {
    color: ${props => props.theme.primaryColor};
    text-decoration: none;
    font-weight: ${props => props.theme.fontWeightHeavy};

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Line = styled.div`
  margin-top: 6px;
  width: 100%;
  height: 0px;
  border-bottom: 1px solid ${props => props.theme.secondaryColorLight};
  display: inline-block;
`;

export default function PendingWire({ wire }) {
  return (
    <Container>
      <CaptionRow>
        <PendingBadge>Pending Review</PendingBadge>
      </CaptionRow>
      <WireTitle wire={wire} />
      {wire.category && (
        <CategoryInfo>
          <span className="category-label">Category: </span>
          <a href={`/categories/${wire.category.slug}`} data-event-type="click" data-event="link_click">
            {wire.category.name}
          </a>
        </CategoryInfo>
      )}
      {wire.updatedAt && (
        <LastSaved>
          {`Last saved on ${moment(wire.updatedAt).format('MMMM Do YYYY @ h:mm:ss a')}`}
        </LastSaved>
      )}
      <Line />
    </Container>
  );
}

PendingWire.propTypes = {
  wire: PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    title: PropTypes.string.isRequired,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    approvalStatus: PropTypes.string,
    isSponsored: PropTypes.bool.isRequired,
    userId: PropTypes.number.isRequired,
    ownedByCurrentUser: PropTypes.bool.isRequired,
    category: PropTypes.shape({
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  }).isRequired,
};
